import { useId, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronDown, Loader2 } from "lucide-react";
import {
  PsvMonitoringProgramStatus,
  STANDARD_RULE_NAME,
} from "@api/shared-runtime/psv-monitoring";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useTRPC } from "@/trpc";
import { DiscardChangesDialog } from "./discard-changes-dialog";
import {
  ENABLED_TOAST,
  MONITORING_COPY,
  standardPausedMessage,
} from "./monitoring-constants";
import {
  describeMissingStates,
  plural,
  summarizeSources,
} from "./monitoring-format";
import { allSourcesDraft, familiesMissingStates } from "./monitoring-sources";
import type {
  MonitoringProgram,
  MonitoringTarget,
  PoolMonitoring,
  SourceDraft,
} from "./monitoring-types";
import { SourceSelector } from "./source-selector";
import { useInvalidateMonitoring } from "./use-monitoring-invalidation";

type Props = {
  organizationBusinessUnitId: string;
  /** Provider or pool name shown in the title. */
  subjectName: string;
  target: MonitoringTarget;
  onClose: () => void;
};

function defaultRule(rules: MonitoringProgram[]): MonitoringProgram {
  return rules.find((rule) => rule.isStandard) ?? rules[0];
}

/** Shared by the provider card and the pool bar. Mount it only while open so
 * its draft state resets each time. */
export function EnableMonitoringDialog({
  organizationBusinessUnitId,
  subjectName,
  target,
  onClose,
}: Props) {
  const trpc = useTRPC();
  const invalidate = useInvalidateMonitoring();
  const isPool = "providerPoolId" in target;
  const [sources, setSources] = useState(allSourcesDraft);
  const [chosenRuleId, setChosenRuleId] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [initialSourcesKey] = useState(() => JSON.stringify(allSourcesDraft()));
  const acknowledgeId = useId();

  const programsQuery = useQuery(
    trpc.psv.monitoring.listPrograms.queryOptions({
      organizationBusinessUnitId,
    }),
  );
  const poolQuery = useQuery({
    ...trpc.psv.monitoring.getPoolMonitoring.queryOptions({
      organizationBusinessUnitId,
      providerPoolId: isPool ? target.providerPoolId : "",
    }),
    enabled: isPool,
  });
  const enable = useMutation(
    trpc.psv.monitoring.enableMonitoring.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        toast.success(ENABLED_TOAST);
        onClose();
      },
    }),
  );

  const activeRules = (programsQuery.data ?? []).filter(
    (rule) => rule.status === PsvMonitoringProgramStatus.Active,
  );
  // With no active rule the dialog sets up the standard rule, unless it
  // exists but is paused: a BU has one, so it must be resumed instead.
  const pausedStandardRule =
    activeRules.length === 0
      ? programsQuery.data?.find(
          (rule) =>
            rule.isStandard &&
            rule.status === PsvMonitoringProgramStatus.Paused,
        )
      : undefined;
  const needsStandardSetup =
    !!programsQuery.data && activeRules.length === 0 && !pausedStandardRule;
  const selectedRule =
    activeRules.find((rule) => rule.id === chosenRuleId) ??
    (activeRules.length > 0 ? defaultRule(activeRules) : undefined);
  const sourcesBlocker = needsStandardSetup
    ? standardSetupBlocker(sources)
    : null;
  const canSubmit =
    !!programsQuery.data &&
    !enable.isPending &&
    (!isPool || acknowledged) &&
    (needsStandardSetup ? sourcesBlocker === null : !!selectedRule);

  // Only the first-time setup holds work worth protecting.
  const isDirty =
    needsStandardSetup && JSON.stringify(sources) !== initialSourcesKey;

  function requestClose() {
    if (isDirty && !enable.isPending) {
      setConfirmingDiscard(true);
      return;
    }
    onClose();
  }

  function submit() {
    enable.mutate({
      organizationBusinessUnitId,
      target,
      ...(needsStandardSetup
        ? { standardRuleSources: sources }
        : { programId: selectedRule?.id }),
    });
  }

  return (
    <Dialog open onOpenChange={(open) => !open && requestClose()}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-xl">
        <DialogHeader className="border-b p-4 pr-12 sm:p-6 sm:pr-12">
          <DialogTitle className="leading-snug">
            Turn on monthly monitoring for {subjectName}
          </DialogTitle>
          <DialogDescription>{MONITORING_COPY.firstChecks}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
          {programsQuery.isPending && (
            <div role="status" aria-busy="true">
              <span className="sr-only">Loading monitoring rules…</span>
              <Skeleton aria-hidden className="h-5 w-72 max-w-full" />
            </div>
          )}
          {programsQuery.isError && (
            <Alert role="alert">
              <AlertDescription>
                Couldn't load monitoring rules.{" "}
                <Button
                  variant="link"
                  className="h-auto p-0"
                  onClick={() => void programsQuery.refetch()}
                >
                  Try again
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {pausedStandardRule && (
            <Alert>
              <AlertDescription>
                {standardPausedMessage(pausedStandardRule.name)}
              </AlertDescription>
            </Alert>
          )}
          {needsStandardSetup && (
            <div className="space-y-3">
              <p className="text-sm">
                Choose what <strong>{STANDARD_RULE_NAME}</strong> checks. Every
                provider you turn on later is added to this rule.
              </p>
              <SourceSelector value={sources} onChange={setSources} />
            </div>
          )}
          {selectedRule && activeRules.length === 1 && (
            <RuleSummary rule={selectedRule} />
          )}
          {selectedRule && activeRules.length > 1 && (
            <RuleSelect
              rules={activeRules}
              value={selectedRule.id}
              onChange={setChosenRuleId}
            />
          )}
          {poolQuery.data && <PoolCounts pool={poolQuery.data} />}
          <BillingDisclosure />
          {isPool && (
            <div className="flex items-start gap-2">
              <Checkbox
                id={acknowledgeId}
                checked={acknowledged}
                onCheckedChange={(checked) => setAcknowledged(checked === true)}
              />
              <Label
                htmlFor={acknowledgeId}
                className="font-normal leading-snug"
              >
                {MONITORING_COPY.poolAcknowledgement}
              </Label>
            </div>
          )}
          {enable.isError && (
            <Alert role="alert">
              <AlertDescription>{enable.error.message}</AlertDescription>
            </Alert>
          )}
        </div>
        <div className="space-y-3 border-t p-4 sm:p-6">
          <p className="text-xs text-muted-foreground">
            {MONITORING_COPY.billing}
          </p>
          {sourcesBlocker && (
            <p role="status" className="text-sm font-medium">
              {sourcesBlocker}
            </p>
          )}
          <DialogFooter className="mt-0">
            <Button variant="outline" onClick={requestClose}>
              Cancel
            </Button>
            <Button disabled={!canSubmit} onClick={submit}>
              {enable.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {MONITORING_COPY.turnOn}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
      {confirmingDiscard && (
        <DiscardChangesDialog
          onKeepEditing={() => setConfirmingDiscard(false)}
          onDiscard={onClose}
        />
      )}
    </Dialog>
  );
}

/** Why the standard rule's checks can't be saved yet, shown beside the
 * disabled button; null when they can. */
function standardSetupBlocker(sources: SourceDraft[]): string | null {
  if (sources.length === 0) {
    return MONITORING_COPY.sourceRequired;
  }
  const missingStates = familiesMissingStates(sources);
  return missingStates.length > 0 ? describeMissingStates(missingStates) : null;
}

/** The overlap rule is secondary to the billing line, so it sits behind a
 * disclosure instead of stacking another paragraph in the dialog. */
function BillingDisclosure() {
  return (
    <Collapsible className="group">
      <CollapsibleTrigger className="-ml-2 flex min-h-11 items-center gap-1 rounded-md px-2 text-sm font-medium text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary sm:min-h-8">
        {MONITORING_COPY.howBillingWorks}
        <ChevronDown
          aria-hidden="true"
          className="size-4 transition-transform group-data-[state=open]:rotate-180"
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-1 text-sm text-muted-foreground">
        {MONITORING_COPY.overlap}
      </CollapsibleContent>
    </Collapsible>
  );
}

function RuleSummary({ rule }: { rule: MonitoringProgram }) {
  return (
    <p className="text-sm">
      Rule: <strong>{rule.name}</strong>{" "}
      <span className="text-muted-foreground">
        ({summarizeSources(rule.sources)})
      </span>
    </p>
  );
}

function RuleSelect({
  rules,
  value,
  onChange,
}: {
  rules: MonitoringProgram[];
  value: string;
  onChange: (programId: string) => void;
}) {
  const labelId = useId();
  return (
    <div className="space-y-1">
      <Label id={labelId}>{MONITORING_COPY.chooseRule}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-labelledby={labelId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {rules.map((rule) => (
            <SelectItem key={rule.id} value={rule.id}>
              {rule.name} ({summarizeSources(rule.sources)})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function PoolCounts({ pool }: { pool: PoolMonitoring }) {
  const startCount = Math.max(
    pool.memberCount - pool.unavailableCount - pool.monitoredCount,
    0,
  );
  return (
    <div className="space-y-1 text-sm">
      <p>
        {plural({ count: startCount, noun: "provider" })} will start
        {pool.monitoredCount > 0 &&
          ` · ${pool.monitoredCount} already monitored`}
      </p>
      {pool.unavailableCount > 0 && (
        <p className="text-muted-foreground">
          {plural({ count: pool.unavailableCount, noun: "member" })} can't be
          monitored yet: no credentials recorded here, a frozen credential file,
          or no live affiliation
        </p>
      )}
    </div>
  );
}
